import { alcanzaNivel } from "./stock/stockFormato";
import { SOLAPAS_STOCK, TECNICA_RUTAS } from "./tecnicaRutas";

/**
 * Quién ve qué en Técnica: la única fuente del menú (tecnicaMenu.js), la guardia de rutas
 * (TecnicaRuta.jsx), la entrada a /admin/tecnica y el ítem «Técnica» del Navbar general.
 *
 * Si cada uno calculara por su lado se contradirían, que es lo que pasaba entre el Navbar
 * (RRHH y OPERADOR no veían Técnica) y el hub (que decidía las solapas por rol). Todo es puro,
 * sin React ni API, para poder probarlo solo. El backend vuelve a controlar cada llamada: esto
 * sólo evita mostrar lo que daría 403.
 */

// Espeja TecnicaAuthChecker.java: sólo estos roles entran a las órdenes sin fila en tecnica_authorization.
// RRHH y OPERADOR entran únicamente si un administrador los autoriza en Configuración › Autorizaciones.
const ROLES_ORDENES = ["ROLE_ADMIN", "ROLE_TECNICO"];

// /employee/tecnicos le pregunta al auth-server (/user/by-roles) con el token de quien opera, y ese
// endpoint sólo acepta estos roles (UserController del auth-server). A un ROLE_USER autorizado en
// Técnica el backend lo deja entrar a las órdenes, pero la lista de técnicos le vuelve 403.
const ROLES_LISTAN_TECNICOS = ["ROLE_ADMIN", "ROLE_SUPERVISOR", "ROLE_JEFE_AREA", "ROLE_OPERADOR", "ROLE_RRHH", "ROLE_TECNICO"];

/** ¿El rol puede pedir la lista de técnicos? Sin ella no se puede elegir a quién asignar una orden. */
export const puedeListarTecnicos = (rol) => ROLES_LISTAN_TECNICOS.includes(rol);

/** El porqué del selector de técnicos deshabilitado (Nueva orden y Asignar técnicos). */
export const TEXTO_SIN_LISTA_TECNICOS = "La asignación de técnicos la hace la oficina: tu rol no puede consultar la lista de técnicos.";

/**
 * @param {{ rol: string, modulos: string[], isTecnicaAuthorized: boolean, tecnicaStockNivel: (string|null),
 *           tecnicaScope?: (string|null) }} ctx `tecnicaScope` es el alcance de la autorización de Técnica
 *           ("FULL", "AREA" o "SOLICITUD": sólo Solicitudes, para quien recibe los reclamos)
 */
export function permisosTecnica({ rol, modulos, isTecnicaAuthorized, tecnicaStockNivel, tecnicaScope = null }) {
    const tieneModulo = Array.isArray(modulos) && modulos.includes("TECNICA");
    const esTecnico = rol === "ROLE_TECNICO";
    const ordenesPorRol = tieneModulo && ROLES_ORDENES.includes(rol);
    // «Solo solicitudes» (alcance SOLICITUD): ve y carga Solicitudes y nada más de Técnica. Espeja
    // TecnicaAuthChecker.hasTecnicaAccess, que lo deja afuera de todo menos de hasSolicitudesAccess. El ADMIN y el
    // técnico entran por el rol, así que una fila de autorización no los restringe.
    const soloSolicitudes = tieneModulo && !ordenesPorRol && !!isTecnicaAuthorized && tecnicaScope === "SOLICITUD";
    const ordenes = !soloSolicitudes && (ordenesPorRol || (tieneModulo && !!isTecnicaAuthorized));
    const nivelStock = tieneModulo ? (tecnicaStockNivel || null) : null;
    return {
        tieneModulo,
        esTecnico,
        nivelStock,
        ordenesPorRol,                         // entra a las órdenes por el rol, sin depender de /me
        soloSolicitudes,                       // sólo Solicitudes (ver, cargar y buscar clientes para cargarlas)
        ordenes,
        crearOrden: ordenes && !esTecnico,     // POST/PUT niegan el scope TECNICO (OrdenTrabajoController:90,101)
        clientes: ordenes && !esTecnico,       // TecnicaClienteController: misma regla que crear órdenes (oficina, no el scope TECNICO)
        // TecnicaSolicitudController: la oficina las carga y las verifica; «solo solicitudes» las ve y las carga.
        solicitudes: (ordenes && !esTecnico) || soloSolicitudes,
        // Tomarlas, aprobarlas, rechazarlas, corregirlas y generar la orden, y abrir la ficha del cliente: sólo la oficina.
        gestionarSolicitudes: ordenes && !esTecnico,
        recorridos: ordenes && !esTecnico,     // TecnicaRecorridoController: oficina; el técnico ve lo suyo en «Mis órdenes»
        estadisticas: ordenes && !esTecnico,   // /stats niega el scope TECNICO (:184,:192)
        // Cancelar y eliminar una orden: el ADMIN o quien tenga la autorización con alcance Completo (no Área ni técnico).
        // Espeja OrdenTrabajoController.ADMIN_O_COMPLETO.
        cancelarEliminar: ordenes && !esTecnico && (rol === "ROLE_ADMIN" || tecnicaScope === "FULL"),
        stock: (requerido) => !!nivelStock && alcanzaNivel(nivelStock, requerido),
        miStock: tieneModulo,                  // TecnicaStockAuth.miStock: módulo + legajo; el legajo lo dice el backend
        miStockEnMenu: tieneModulo && esTecnico,
        configuracion: tieneModulo && rol === "ROLE_ADMIN",
        listarTecnicos: tieneModulo && puedeListarTecnicos(rol),
    };
}

const TEXTO_ORDENES = "Las órdenes de trabajo las ven Administración, los técnicos y quien tenga autorización de Técnica.";
const TEXTO_SOLO_SOLICITUDES = "Tu autorización de Técnica es para ver y cargar solicitudes. El resto lo maneja la oficina.";

/** El porqué de lo que es de la oficina: distinto para el técnico, para quien sólo tiene Solicitudes y para el resto. */
const textoOficina = (p, textoTecnico) => {
    if (p.esTecnico && textoTecnico) return { texto: textoTecnico };
    if (p.soloSolicitudes) return { texto: TEXTO_SOLO_SOLICITUDES };
    return { texto: TEXTO_ORDENES };
};
const TEXTO_STOCK = "No tenés acceso al stock de Técnica.";
const DETALLE_STOCK = "El permiso lo da un administrador en Técnica › Configuración › Permisos de stock.";
const TEXTO_KITS = "Las plantillas de kit las administra alguien con nivel Gestión.";

const LABELS_STOCK = {
    saldos: "Saldos",
    remitos: "Remitos",
    movimientos: "Movimientos",
    catalogo: "Catálogo",
    herramientas: "Herramientas",
    kits: "Kits",
    tecnicos: "Técnicos",
};

/** Una solapa de stock: CONSULTA alcanza para verla, salvo Kits (misma regla que TecnicaStockPage). */
const seccionStock = (id) => {
    const requerido = id === "kits" ? "GESTION" : "CONSULTA";
    return {
        esStock: true,
        label: () => LABELS_STOCK[id],
        ruta: TECNICA_RUTAS.stock(id),
        puede: (p) => p.stock(requerido),
        // Quien tiene algún nivel pero no Gestión no es «sin acceso al stock»: le falta el nivel de Kits.
        sinPermiso: (p) => (id === "kits" && p.nivelStock
            ? { texto: TEXTO_KITS }
            : { texto: TEXTO_STOCK, detalle: DETALLE_STOCK }),
    };
};

/**
 * id → { label(p), ruta, puede(p), sinPermiso(p), esStock? }. `sinPermiso` es el porqué que ve
 * quien entra por la URL a algo que no le corresponde, en vez de una pantalla en blanco o un 403.
 * El módulo y los avisos de /me (ERROR_ME, LEGAJO_DUPLICADO) los resuelve antes TecnicaSinPermiso.
 */
export const SECCIONES_TECNICA = {
    clientes: {
        label: () => "Clientes",
        ruta: TECNICA_RUTAS.clientes,
        puede: (p) => p.clientes,
        sinPermiso: (p) => textoOficina(p, "La base de clientes la administra la oficina. Como técnico, ves los datos del cliente dentro de tus órdenes."),
    },
    solicitudes: {
        label: () => "Solicitudes",
        ruta: TECNICA_RUTAS.solicitudes,
        puede: (p) => p.solicitudes,
        sinPermiso: (p) => textoOficina(p, "Las solicitudes de servicio las carga y las verifica la oficina. Como técnico, ves tus órdenes en Mis órdenes."),
    },
    ordenes: {
        label: (p) => (p.esTecnico ? "Mis órdenes" : "Órdenes"),
        ruta: TECNICA_RUTAS.ordenes,
        puede: (p) => p.ordenes,
        sinPermiso: (p) => textoOficina(p),
    },
    // No es un ítem del menú: se llega desde «Órdenes» (botón «Recorridos»), que es donde la oficina planifica el día.
    recorridos: {
        label: () => "Recorridos",
        ruta: TECNICA_RUTAS.recorridos(),
        puede: (p) => p.recorridos,
        sinPermiso: (p) => textoOficina(p, "Los recorridos los arma la oficina. Como técnico, ves tus órdenes en Mis órdenes."),
    },
    nuevaOrden: {
        label: () => "Nueva orden",
        ruta: TECNICA_RUTAS.nuevaOrden,
        puede: (p) => p.crearOrden,
        sinPermiso: (p) => textoOficina(p, "Las órdenes las crea y las edita la oficina. Como técnico, ves las tuyas en Mis órdenes."),
    },
    estadisticas: {
        label: () => "Estadísticas",
        ruta: TECNICA_RUTAS.estadisticas,
        puede: (p) => p.estadisticas,
        sinPermiso: (p) => textoOficina(p, "Las estadísticas de órdenes son para la oficina."),
    },
    ...Object.fromEntries(SOLAPAS_STOCK.map((id) => [id, seccionStock(id)])),
    // CONSULTA entra y ve sólo los agregados (el detalle lo ven Pañol, lo que declaró, y Gestión).
    // El técnico sin nivel ve los suyos en Mi stock, no acá.
    incidentes: {
        esStock: true,
        label: () => "Incidentes",
        ruta: TECNICA_RUTAS.incidentes,
        puede: (p) => p.stock("CONSULTA"),
        sinPermiso: () => ({ texto: TEXTO_STOCK, detalle: DETALLE_STOCK }),
    },
    // Sólo Gestión: regularizar mueve stock con motivo. Quien tiene otro nivel entra al stock pero no acá.
    diferencias: {
        esStock: true,
        label: () => "Diferencias",
        ruta: TECNICA_RUTAS.diferencias,
        puede: (p) => p.stock("GESTION"),
        sinPermiso: (p) => (p.nivelStock
            ? { texto: "Las diferencias las resuelve alguien con nivel Gestión." }
            : { texto: TEXTO_STOCK, detalle: DETALLE_STOCK }),
    },
    miStock: {
        esStock: true,
        label: () => "Mi stock",
        ruta: TECNICA_RUTAS.miStock,
        puede: (p) => p.miStock,
        // Mi stock sólo pide el módulo, y eso TecnicaSinPermiso lo dice antes de llegar acá.
        sinPermiso: () => ({ texto: "Tu empresa no tiene el módulo Técnica." }),
    },
    configuracion: {
        label: () => "Configuración",
        ruta: TECNICA_RUTAS.configuracion(),
        puede: (p) => p.configuracion,
        sinPermiso: () => ({ texto: "La configuración de Técnica la maneja un administrador." }),
    },
};

/** La sección o un Error: un id desconocido es un typo, y no tiene que convertirse en «sin permiso». */
function seccion(id) {
    const s = SECCIONES_TECNICA[id];
    if (!s) throw new Error(`Sección de Técnica desconocida: ${id}`);
    return s;
}

export function puedeVerSeccion(p, id) {
    return !!seccion(id).puede(p);
}

export function etiquetaSeccion(p, id) {
    return seccion(id).label(p);
}

export function textoSinPermiso(p, id) {
    return seccion(id).sinPermiso(p);
}

export function esSeccionStock(id) {
    return !!seccion(id).esStock;
}

/**
 * ¿La negativa de `id` puede deberse a que falló /me? Sólo entonces TecnicaSinPermiso dice «Recargá
 * la página»: si la niega el rol (Nueva orden y Estadísticas para el técnico, Configuración para
 * quien no es ADMIN), recargar no la destraba y se perdería el porqué real. De /me salen el nivel
 * de stock y la autorización de órdenes de quien no está en ROLES_ORDENES.
 */
export function dependeDeMe(p, id) {
    const s = seccion(id);
    if (s.esStock) return true;
    switch (id) {
        case "configuracion":
            return false;
        case "clientes":
        case "recorridos":
        case "nuevaOrden":
        case "estadisticas":
            // «Solo solicitudes» ya se sabe por /me: recargar no le abre lo que su autorización no incluye.
            return !p.esTecnico && !p.ordenesPorRol && !p.soloSolicitudes;
        case "solicitudes":
            return !p.esTecnico && !p.ordenesPorRol;
        case "ordenes":
            return !p.ordenesPorRol && !p.soloSolicitudes;
        default:
            return true;
    }
}

/**
 * La primera sección que la persona puede ver: adonde lleva /admin/tecnica y el ítem «Técnica»
 * del Navbar general. Mi stock sólo cuenta para el técnico: para el resto no está en el menú.
 *
 * @returns {{ id: string, label: string, ruta: string } | null}
 */
export function rutaInicialTecnica(p) {
    const candidatas = ["ordenes", "solicitudes", "saldos", ...(p.miStockEnMenu ? ["miStock"] : []), "configuracion"];
    const id = candidatas.find((c) => puedeVerSeccion(p, c));
    if (!id) return null;
    return { id, label: etiquetaSeccion(p, id), ruta: SECCIONES_TECNICA[id].ruta };
}
